import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import Pusher from 'pusher-js';

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  
  // --- ESTADOS DE IDENTIDAD ---
  const [clienteId, setClienteId] = useState(() => {
    return localStorage.getItem('cliente_id') || null;
  });

  // --- ESTADOS DEL CHAT Y UI ---
  const [messages, setMessages] = useState([]);
  const [estado, setEstado] = useState('ESPERANDO_CATEGORIA');
  const [cargando, setCargando] = useState(false);
  const [mostrarBotonEntrega, setMostrarBotonEntrega] = useState(false);

  // --- ESTADOS DEL PEDIDO ---
  const [pedidoId, setPedidoId] = useState(null);
  const [pedidoActual, setPedidoActual] = useState([]);
  const [categoria, setCategoria] = useState(null);
  const [producto, setProducto] = useState(null);
  const [corteSeleccionado, setCorteSeleccionado] = useState(null);
  const [modoEntrega, setModoEntrega] = useState(null);
  const [datosEnvio, setDatosEnvio] = useState({
    nombre: '', 
    direccion: '', 
    telefono: '',
    tipoDireccion: null, 
    coordenadas: null,
    puntoReferencia: '', 
    esUbicacionExacta: false
  });

 // --- CONFIGURACIÓN DE RUTAS: USAMOS LAS RUTAS DIRECTAS (con .php) ---
const URL_BASE = 'https://clavelinacarnes.com/clavelina-backend-php/src/Routes';
const URL_BASE_API = URL_BASE;      // ← para compatibilidad
const URL_BASE_ROUTES = URL_BASE;   // ← para compatibilidad

  // --- REFERENCIAS TÉCNICAS ---
  const pusherRef = useRef(null);
  
  // --- EFECTO A: RECUPERACIÓN DE SESIÓN (usando ruta directa .php) ---
  useEffect(() => {
    const cargarSesionDelServidor = async () => {
      if (!clienteId || clienteId === "null" || clienteId === "undefined") {
        setMessages([]);
        setEstado('SALUDO');
        return;
      }

      try {
        setCargando(true);
        console.log("🔌 Recuperando sesión desde PHP para:", clienteId);

        // CAMBIO: usar la ruta directa con .php
        const res = await fetch(`${URL_BASE}/chat/recuperar_sesion.php?cliente_id=${clienteId}`);
        if (!res.ok) throw new Error('Error en la respuesta del servidor');
        
        const textoCrudo = await res.text();
        console.log("📝 Sesión cruda recibida de PHP:", textoCrudo);

        let data = {};
        if (textoCrudo.includes('{') && textoCrudo.includes('}')) {
          const inicioJson = textoCrudo.indexOf('{');
          const finJson = textoCrudo.lastIndexOf('}') + 1;
          data = JSON.parse(textoCrudo.substring(inicioJson, finJson));
        } else {
          throw new Error("La respuesta de recuperación no contiene un JSON válido.");
        }

        if (data.success) {
          if (data.estado) setEstado(data.estado);

          if (data.historial && Array.isArray(data.historial)) {
            const historialNormalizado = data.historial.map(msg => ({
              id: msg.id || `hist-${Math.random().toString(36).substr(2, 9)}`,
              sender: msg.emisor === 'lina' ? 'bot' : 'user',
              text: msg.mensaje,
              timestamp: msg.fecha_envio || new Date(),
              opciones: msg.opciones || []
            }));
            setMessages(historialNormalizado);
          }

          if (data.datos_pedido) {
            const d = data.datos_pedido;
            if (d.pedidoActual) setPedidoActual(d.pedidoActual);
            if (d.ultimo_pedido_id) setPedidoId(d.ultimo_pedido_id);
            if (d.modoEntrega) setModoEntrega(d.modoEntrega);
          }

          console.log("✅ Sesión recuperada con éxito desde PHP");
        }
      } catch (err) {
        console.error("❌ Error al recuperar sesión:", err);
        if (messages.length === 0) {
          setEstado('SALUDO');
        }
      } finally {
        setCargando(false);
      }
    };

    cargarSesionDelServidor();
  }, [clienteId]);

  // --- EFECTO B: SINCRONIZACIÓN DE IDENTIDAD POR STORAGE ---
  useEffect(() => {
    const checkAuth = () => {
      const idEnStorage = localStorage.getItem('cliente_id');
      if (idEnStorage !== clienteId) {
        console.log("🔄 Sincronizando identidad de storage:", idEnStorage ? "ID detectado" : "Sesión cerrada");
        setClienteId(idEnStorage); 
      }
    };

    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, [clienteId]);

  // Sincronización secundaria por login de usuario interno
  useEffect(() => {
    const checkUserLogin = () => {
      const usuarioLogueado = JSON.parse(localStorage.getItem('user'));
      if (usuarioLogueado && usuarioLogueado.id && usuarioLogueado.id !== clienteId) {
        console.log("🔄 Sincronizando Chat con nueva identidad de usuario...");
        setClienteId(usuarioLogueado.id);
        localStorage.setItem('cliente_id', usuarioLogueado.id);
      }
    };
    checkUserLogin();
  }, [clienteId]);

  // --- MOTOR DE MENSAJES (Helper) ---
  const agregarMensajeBot = useCallback((texto, opciones = []) => {
    setMessages(prev => [...prev, {
      id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      sender: 'bot',
      text: texto,
      opciones: opciones,
      timestamp: new Date()
    }]);
  }, []);

  // --- ACCIÓN: COMUNICACIÓN GENERAL CON LINA (Enviar) ---
const enviar = async (texto, datosInmediatos = {}, ignorarCargando = false) => {
  if (!texto.trim() || (cargando && !ignorarCargando)) return;

  setMessages(prev => [...prev, { 
    id: `user-${Date.now()}`, 
    sender: 'user', 
    text: texto, 
    timestamp: new Date() 
  }]);

  if (!ignorarCargando) setCargando(true);

  const pedidoAEnviar = (pedidoActual.length === 0 && pedidoId) 
      ? null 
      : (datosInmediatos.pedidoActual || pedidoActual);

  const payload = {
    cliente_id: clienteId,
    mensaje: texto,
    estado_actual: estado,
    datos_anteriores: {
      ultimo_pedido_id: pedidoId,
      pedidoActual: pedidoAEnviar,
      categoria: datosInmediatos.categoria || categoria,
      producto: datosInmediatos.producto || producto,
      corteSeleccionado: datosInmediatos.corteSeleccionado || corteSeleccionado,
      modoEntrega: datosInmediatos.modoEntrega || modoEntrega,
      datosEnvio: datosEnvio
    }
  };

  try {
    console.log("🚀 === INICIO ENVÍO MENSAJE ===");
  
    console.log("📦 Payload original:", payload);
  
    // Verificar JSON antes de enviar
    let payloadJSON;
    try {
      payloadJSON = JSON.stringify(payload);
      console.log("📤 Payload JSON listo:", payloadJSON);
    } catch (jsonErr) {
      console.error("❌ ERROR serializando payload:", jsonErr);
      throw jsonErr;
    }
  
    const url = "https://clavelinacarnes.com/clavelina-backend-php/public/index.php/chat/mensaje";
    console.log("🌐 URL destino:", url);
  
    console.log("📡 Enviando fetch...");
  
    const res = await fetch(url, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json' 
      },
      body: payloadJSON
    });
  
    console.log("📊 STATUS HTTP:", res.status);
    console.log("📊 STATUS OK:", res.ok);
    console.log("📊 STATUS TEXT:", res.statusText);
  
    const textoCrudo = await res.text();
    console.log("📝 RESPUESTA CRUDA COMPLETA:", textoCrudo);
  
    if (!res.ok) {
      throw new Error(`Error servidor ${res.status}: ${textoCrudo}`);
    }
  
    let data = {};
  
    try {
      if (textoCrudo.includes('{') && textoCrudo.includes('}')) {
        const inicioJson = textoCrudo.indexOf('{');
        const finJson = textoCrudo.lastIndexOf('}') + 1;
        data = JSON.parse(textoCrudo.substring(inicioJson, finJson));
      } else {
        throw new Error("La respuesta no contiene JSON válido");
      }
    } catch (parseErr) {
      console.error("❌ ERROR parseando JSON:", parseErr);
      throw parseErr;
    }
  
    console.log("✅ DATA FINAL PARSEADA:", data);
  
    if (data.nuevo_estado) setEstado(data.nuevo_estado);
    if (data.estado_actual) setEstado(data.estado_actual);
  
    const textoNormalizado = texto.toLowerCase().trim();
  
    if (
      textoNormalizado === 'ir al inicio' ||
      textoNormalizado === 'nuevo pedido'
    ) {
      console.log("🔄 Reset de pedido ejecutado");
      resetParaNuevoPedido();
      return;
    }
  
    console.log("✔️ === FIN EXITOSO ENVÍO MENSAJE ===");
  
  } catch (err) {
    console.error("❌ ERROR COMPLETO EN COMUNICACIÓN PHP:", err);
  } finally {
    console.log("🔚 FIN BLOQUE FETCH (finally ejecutado)");
    setCargando(false);
  }
};

  // --- ACCIÓN: SUBIDA DE COMPROBANTE (CORREGIDO) ---
  const subirComprobante = async (archivo) => {
    if (!archivo) return;
    setCargando(true);

    const formData = new FormData();
    formData.append('comprobante', archivo);
    formData.append('cliente_id', clienteId);
    formData.append('pedido_id', pedidoId);

    try {
      // CORREGIDO: Eliminamos el ".php" y lo adaptamos al enrutador amigable
      const resSubida = await fetch(`${URL_BASE_ROUTES}/admin/subir_comprobante`, {
        method: 'POST',
        body: formData,
      });

      if (!resSubida.ok) throw new Error("Error al subir archivo al servidor");

      const textoCrudo = await resSubida.text();
      let dataSubida = {};
      
      if (textoCrudo.includes('{') && textoCrudo.includes('}')) {
        const inicioJson = textoCrudo.indexOf('{');
        const finJson = textoCrudo.lastIndexOf('}') + 1;
        dataSubida = JSON.parse(textoCrudo.substring(inicioJson, finJson));
      }

      if (dataSubida.success) {
        console.log("📸 Imagen subida con éxito:", dataSubida.url);
        await enviar(dataSubida.url, {}, true);
      } else {
        throw new Error(dataSubida.error || "Falla en la respuesta del servidor");
      }
    } catch (err) {
      console.error("❌ Error en subida de comprobante:", err);
      agregarMensajeBot("⚠️ No pude procesar la imagen. Por favor, intenta subirla nuevamente.");
    } finally {
      setCargando(false);
    }
  };

  const resetParaNuevoPedido = useCallback(() => {
    console.log("🧹 Borrado absoluto: Storage + Cookies...");
    
    // 1. Limpieza de LocalStorage
    localStorage.clear();

    // 2. Limpieza fulminante de Cookies desde JavaScript
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // 3. Limpiamos estados de React
    setClienteId(null);
    setPedidoId(null);
    setPedidoActual([]);
    setCategoria(null);
    setProducto(null);
    setCorteSeleccionado(null);
    setModoEntrega(null);
    setMessages([]);
    setEstado('SALUDO'); 
    setMostrarBotonEntrega(false);

    // 4. Redirección física
    window.location.href = '/';
  }, []);


  // --- PUSHER: CANAL PRINCIPAL DE CHAT CLIENTE ---
  useEffect(() => {
    if (!clienteId) return;
    
    pusherRef.current = new Pusher('ff59d4da6a1a8c01af49', { cluster: 'us2', forceTLS: true });
    const channel = pusherRef.current.subscribe(`chat-${clienteId}`);

    channel.bind('nuevo-mensaje', (data) => {
      console.log("🔥🔥🔥 EVENTO RECIBIDO EN CLIENTE (Canal Chat):", data);
      if (data.estado_actual) setEstado(data.estado_actual);
      
      const d = data.datos_pedido || data; 
      if (d) {
        const idDelPedido = d.pedido_id || d.ultimo_pedido_id || data.ultimo_pedido_id;
        if (idDelPedido) setPedidoId(idDelPedido);
        if (d.pedidoActual) setPedidoActual(d.pedidoActual);
        if (d.categoria) setCategoria(d.categoria);
        if (d.producto) setProducto(d.producto);
        if (d.corte) setCorteSeleccionado(d.corte);
        
        const modo = d.modoEntrega || data.modoEntrega;
        if (modo) setModoEntrega(modo);
      }
    
      const envio = data.datos_envio || data.datosEnvio;
      if (envio) setDatosEnvio(prev => ({ ...prev, ...envio }));
      
      agregarMensajeBot(data.mensaje, data.opciones || []);
    });

    channel.bind('delivery_en_ubicacion', (data) => {
      agregarMensajeBot(`📍 ${data.mensaje || 'El repartidor ha llegado.'}`);
      setMostrarBotonEntrega(true);
    });

    return () => {
      channel.unbind_all();
      pusherRef.current.unsubscribe(`chat-${clienteId}`);
    };
  }, [clienteId, agregarMensajeBot]);

  // --- PUSHER: CANAL EXCLUSIVO DE SEGUIMIENTO DE PEDIDO (Cambios de Admin) ---
  useEffect(() => {
    if (pedidoId && pusherRef.current) {
      const deliveryChannel = pusherRef.current.subscribe(`cliente-pedido-${pedidoId}`);
      
      deliveryChannel.bind('estado-actualizado', (data) => {
        console.log("📢 CAMBIO ESTADO PEDIDO EN TIEMPO REAL:", data);
        const nuevoEstado = data.estado ? data.estado.toUpperCase() : '';
        if (nuevoEstado) setEstado(nuevoEstado);

        let mensajeParaMostrar = data.mensaje;
        if (nuevoEstado === 'PENDIENTE') {
          mensajeParaMostrar = "⭐ ¡Confirmado! El administrador ya recibió tu pago y lo marcó como verificado. ¡Tu pedido entra ahora mismo a preparación! 🥩🔥";
        } else if (nuevoEstado === 'EN_RUTA') {
          mensajeParaMostrar = "🛵 ¡Tu pedido ya va en camino! Atento al teléfono que el repartidor está cerca. ✨";
        } else if (nuevoEstado === 'LISTO') {
          mensajeParaMostrar = "🛍️ ¡Pedido listo! Ya puedes pasar por la tienda a retirarlo. ¡Te esperamos! 🏪";
        }

        agregarMensajeBot(mensajeParaMostrar);
      });

      return () => {
        if (pusherRef.current) pusherRef.current.unsubscribe(`cliente-pedido-${pedidoId}`);
      };
    }
  }, [pedidoId, agregarMensajeBot]);

  // --- ACCIONES DE CIERRE DE PEDIDO (CORREGIDO) ---
  const confirmarRecepcion = async (idManual) => {
    const idFinal = idManual || pedidoId;
    if (!idFinal) return;
    setMostrarBotonEntrega(false);
    try {
      // CORREGIDO: Ajustado al formato amigable sin el ".php" final
      const res = await fetch(`${URL_BASE_ROUTES}/chat/confirmar_entrega`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pedido_id: idFinal, cliente_id: clienteId })
      });
      
      if (res.ok) {
        agregarMensajeBot('✅ ¡Excelente! Disfruta tu pedido. 🥩✨');
        setTimeout(() => {
          setEstado('CHAT_MENU_POST_PEDIDO'); 
        }, 1000);
      }
    } catch (err) { 
      console.error("Error al confirmar en PHP:", err); 
      setMostrarBotonEntrega(true); 
    }
  };

  const enviarLlegadaDirecta = async () => {
    setMessages(prev => [...prev, { id: `arr-${Date.now()}`, sender: 'user', text: "🏪 ¡Ya estoy en la tienda!", timestamp: new Date() }]);
    try {
      // La variable URL_BASE_API ya es dinámica gracias a la Parte 1
      await fetch(`${URL_BASE_API}/chat/mensaje`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cliente_id: clienteId,
          mensaje: 'llegue_a_tienda',
          estado_actual: estado,
          datos_anteriores: { ultimo_pedido_id: pedidoId, modoEntrega: 'retiro' }
        })
      });
    } catch (err) { console.error("Error llegada en PHP:", err); }
  };

  // Guardado de emergencia (Beacon - CORREGIDO)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!clienteId) return;
      const estadoCritico = { pedidoActual, estado, datosEnvio, clienteId };
      
      // CORREGIDO: Se quita la extensión física para que coincida con el index.php en producción
      navigator.sendBeacon(
        `${URL_BASE_ROUTES}/chat/guardar_emergencia`,
        JSON.stringify({ cliente_id: clienteId, estado_critico: estadoCritico })
      );
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [pedidoActual, estado, clienteId, datosEnvio]);

  return (
    <ChatContext.Provider value={{
      messages, setMessages, estado, setEstado, pedidoActual, setPedidoActual,
      clienteId, datosEnvio, setDatosEnvio, enviar, cargando,
      pedidoId, categoria, producto, corteSeleccionado, modoEntrega,
      confirmarRecepcion, enviarLlegadaDirecta, mostrarBotonEntrega, setMostrarBotonEntrega, resetParaNuevoPedido, subirComprobante
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);